/**
 * Frozen Artwork detail (#artwork/<id>) — READ ONLY.
 *
 * Deterministic preview from the FROZEN canonical payload + immutable metadata.
 * There are intentionally NO edit / delete / burn / transfer / marketplace /
 * move / rotation controls (those are out of #14 scope).
 */
import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { artworkApi, ArtworkApiError, type ArtworkPublic, type ProvenanceEvent } from "../../lib/artwork-api";
import { FrozenArtworkRender } from "../../viewer/FrozenArtworkRender";
import { OwnerModerationBanner } from "../moderation/OwnerModerationBanner";

function shortWallet(w: string): string {
  return w && w.length > 12 ? `${w.slice(0, 6)}…${w.slice(-4)}` : w;
}

function Row({ label, value, testid, mono }: { label: string; value: React.ReactNode; testid?: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/5 py-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/40">{label}</span>
      <span data-testid={testid} className={`text-right text-sm text-white/80 ${mono ? "font-mono break-all" : ""}`}>{value}</span>
    </div>
  );
}

export function FrozenArtworkRoute({ artworkId }: { artworkId: string | null }) {
  const { address } = useAccount();
  const [art, setArt] = useState<ArtworkPublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prov, setProv] = useState<ProvenanceEvent[]>([]);

  useEffect(() => {
    if (!artworkId) {
      setLoading(false);
      setError("Invalid artwork route.");
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    artworkApi
      .getArtwork(artworkId, controller.signal)
      .then((a) => setArt(a))
      .catch((e) => {
        if (controller.signal.aborted) return;
        setError(e instanceof ArtworkApiError ? e.message : "Failed to load artwork");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [artworkId]);

  useEffect(() => {
    if (!artworkId) return;
    const c = new AbortController();
    artworkApi.getProvenance(artworkId, c.signal).then((r) => setProv(r.events)).catch(() => setProv([]));
    return () => c.abort();
  }, [artworkId]);

  const isOwner = !!(address && art && address.toLowerCase() === art.owner.toLowerCase());

  if (loading) {
    return <div data-testid="artwork-loading" className="p-10 text-center font-mono text-sm text-white/50">Loading artwork…</div>;
  }
  if (error || !art) {
    return (
      <div data-testid="artwork-error" className="mx-auto max-w-md p-10 text-center text-red-300">
        <p className="mb-4">{error || "Artwork unavailable."}</p>
        <a href="#profile" className="rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 no-underline">Back to Collection</a>
      </div>
    );
  }

  return (
    <div data-testid="artwork-detail-route" className="mx-auto w-full max-w-4xl px-4 py-8">
      <a href="#profile" className="mb-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-white/50 no-underline hover:text-white/80">← Collection</a>
      <OwnerModerationBanner targetType="artwork" targetId={art.artwork_id} enabled={isOwner} className="mb-6" />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="flex flex-col items-center gap-3">
          {art.content ? (
            <FrozenArtworkRender
              testid="artwork-frozen-preview"
              content={art.content}
              width={art.width}
              height={art.height}
              maxPx={360}
            />
          ) : (
            <div className="text-white/40">Preview unavailable.</div>
          )}
          <span data-testid="artwork-content-hash-badge" className="font-mono text-[10px] text-white/30 break-all">{art.content_hash}</span>
        </div>
        <div>
          <div className="mb-2 flex items-center gap-2">
            <h1 data-testid="artwork-title" className="text-2xl font-semibold text-white">{art.title || "Untitled"}</h1>
            <span data-testid="artwork-status" className="rounded-full border border-teal-300/40 bg-teal-400/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-teal-200">{art.status}</span>
          </div>
          {art.description && <p data-testid="artwork-description" className="mb-4 text-sm text-white/60">{art.description}</p>}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <Row label="Artwork ID" value={art.artwork_id} testid="artwork-id" mono />
            <Row label="Creator" value={shortWallet(art.creator)} testid="artwork-creator" mono />
            <Row label="Owner" value={shortWallet(art.owner)} mono />
            <Row label="Frozen" value={new Date(art.frozen_at).toLocaleString()} testid="artwork-frozen-at" />
            <Row label="Dimensions" value={`${art.width} × ${art.height}`} testid="artwork-dimensions" />
            <Row label="Footprint" value={`${art.footprint} cells`} testid="artwork-footprint" />
            <Row label="Royalty" value={`${art.royalty_percent}%`} testid="artwork-royalty" />
            <Row label="Content version" value={`v${art.content_version}`} />
            {art.placement && (
              <Row
                label="Placement"
                testid="artwork-placement"
                value={`(${art.placement.anchor_x}, ${art.placement.anchor_y}) · rot ${((art.placement.rotation_millideg ?? art.placement.rotation * 1000) / 1000).toFixed(art.placement.rotation_millideg && art.placement.rotation_millideg % 1000 ? 3 : 0)}°${art.placement.background ? ` · bg ${art.placement.background}` : ""}`}
              />
            )}
          </div>
          {isOwner && art.placement && (
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                data-testid="edit-placement-btn"
                href={`#grid?fx=${art.placement.anchor_x + art.width / 2}&fy=${art.placement.anchor_y + art.height / 2}&aid=${art.artwork_id}&edit=1`}
                className="rounded-full bg-teal-400 px-4 py-2 text-sm font-semibold text-black no-underline hover:bg-teal-300"
              >
                Edit Placement on Grid
              </a>
              <a
                data-testid="locate-on-grid-link"
                href={`#grid?fx=${art.placement.anchor_x + art.width / 2}&fy=${art.placement.anchor_y + art.height / 2}&aid=${art.artwork_id}`}
                className="rounded-full border border-white/20 px-4 py-2 text-sm text-white/80 no-underline hover:border-teal-300/40"
              >
                Locate on Grid
              </a>
            </div>
          )}
          <p className="mt-4 text-xs text-white/40">
            This artwork is frozen. Its content and metadata are immutable and cannot be edited, moved-in-content or deleted. Placement (position, rotation, presentation) is mutable and never changes the artwork’s identity or hash.
          </p>

          {/* Provenance — append-only, deterministic */}
          <div data-testid="artwork-provenance" className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
            <h2 className="mb-3 font-mono text-[10px] uppercase tracking-[0.16em] text-white/40">Provenance</h2>
            <ol className="space-y-2">
              {prov.map((e) => (
                <li key={e.seq} data-testid={`provenance-${e.seq}`} className="flex items-start gap-3 text-sm">
                  <span className="mt-0.5 shrink-0 rounded border border-white/10 px-1.5 py-0.5 font-mono text-[9px] text-white/40">#{e.seq}</span>
                  <div>
                    <span className="font-medium text-white/80">{provLabel(e.kind)}</span>
                    <span className="ml-2 font-mono text-[10px] text-white/40">{new Date(e.at).toLocaleString()}</span>
                    {e.kind === "PLACEMENT_UPDATED" && e.before && e.after && (
                      <div className="font-mono text-[10px] text-white/40">
                        ({(e.before as { anchor_x: number }).anchor_x},{(e.before as { anchor_y: number }).anchor_y}) → ({(e.after as { anchor_x: number }).anchor_x},{(e.after as { anchor_y: number }).anchor_y}) · rot {(((e.after as { rotation_millideg?: number }).rotation_millideg ?? 0) / 1000)}°
                      </div>
                    )}
                    {e.kind === "OWNERSHIP_TRANSFERRED" && e.facts && (
                      <div data-testid={`provenance-transfer-${e.seq}`} className="font-mono text-[10px] text-white/40">
                        {shortWallet(String((e.facts as { previous_owner?: string }).previous_owner ?? ""))} → {shortWallet(String((e.facts as { new_owner?: string }).new_owner ?? ""))}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

function provLabel(kind: string): string {
  const m: Record<string, string> = {
    FROZEN: "Created / Frozen",
    INITIAL_PLACEMENT: "First placed",
    PLACEMENT_UPDATED: "Placement updated",
    OWNERSHIP_TRANSFERRED: "Ownership transferred",
  };
  return m[kind] ?? kind;
}
